import { effectiveAnnualUsageKwhForPricing } from '../../helpers/utilityMonthlyDailyToAnnual';
import designServices from '../../services/oneButtonServices/designServices';
import masterServices from '../../services/oneButtonServices/masterServices';
import opportunityInfoService from '../../services/oneButtonServices/opportunityInfoService';
import type { PreContractIntakeForm } from './preContractTypes';
import { UTILITY_OPTIONS } from './preContractTypes';
import { formatSandboxEscalator, resolvePreContractEscalator } from './preContractSandboxPricing';

/** GetCompanies row mapped to Utility_company_id + display name. */
export type PreContractUtilityCompanyCatalogItem = {
    Utility_company_id: string;
    name: string;
};

export type PreContractUtilityPrefill = {
    utilityCompany: string;
    annualUsageKwh: string;
    utilityRate: string;
};

function normalizeCompanyName(name: string): string {
    return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Map opportunity utility name to intake select options when possible. */
export function resolveUtilityCompanyLabel(rawName: string | null | undefined): string {
    const name = String(rawName ?? '').trim();
    if (!name) return '';

    const normalized = normalizeCompanyName(name);
    const exact = UTILITY_OPTIONS.find((option) => normalizeCompanyName(option) === normalized);
    if (exact) return exact;

    const partial = UTILITY_OPTIONS.find((option) => {
        const optionNorm = normalizeCompanyName(option);
        return normalized.includes(optionNorm) || optionNorm.includes(normalized);
    });
    if (partial) return partial;

    if (normalized.includes('united illuminating') || normalized === 'ui') return 'United Illuminating';
    if (normalized.includes('cl&p') || normalized.includes('clp')) return 'Eversource (CL&P)';
    if (normalized.includes('national grid')) return 'National Grid';
    if (normalized.includes('con ed') || normalized.includes('coned')) return 'Con Edison';
    if (normalized.includes('eversource')) return 'Eversource';

    return name;
}

export function utilityCompanySelectOptions(current: string): string[] {
    const trimmed = current.trim();
    if (!trimmed || UTILITY_OPTIONS.includes(trimmed)) return [...UTILITY_OPTIONS];
    return [trimmed, ...UTILITY_OPTIONS];
}

export type UtilityCompanyCatalogStatus = 'loading' | 'ready' | 'empty';

/** Only ever offer what GetCompanies returned for the state — never a hardcoded name. */
export function utilityCompanyOptionsFromCatalog(
    catalog: PreContractUtilityCompanyCatalogItem[],
    status: UtilityCompanyCatalogStatus,
    current: string,
): string[] {
    if (status === 'ready') return catalog.map((item) => item.name);
    // Keep the current value alive while loading so the select does not flash empty.
    if (status === 'loading') return current.trim() ? [current] : [];
    return [];
}

/** Message for states GetCompanies has no utilities for. */
export function utilityCatalogEmptyMessage(stateCode: string): string {
    const state = String(stateCode ?? '').trim().toUpperCase();
    return state
        ? `No utility providers are configured for ${state}.`
        : 'Set the install state to load utility providers.';
}

/** Keep name and `Utility_company_id` in step whenever a utility is picked. */
export function utilityCompanySelectionPatch(
    selectedName: string,
    catalog: PreContractUtilityCompanyCatalogItem[],
): { utilityCompany: string; utilityCompanyId: string } {
    const name = selectedName.trim();
    if (!name) return { utilityCompany: '', utilityCompanyId: '' };
    const match = resolveUtilityCompanyCatalogMatch(name, catalog);
    return {
        utilityCompany: match?.name || name,
        utilityCompanyId: match?.Utility_company_id ?? '',
    };
}

function mapGetCompaniesRow(company: Record<string, unknown>): PreContractUtilityCompanyCatalogItem | null {
    const Utility_company_id = String(
        company.Utility_company_id ??
            company.utility_company_id ??
            company.Value ??
            company.value ??
            '',
    ).trim();
    const name = String(
        company.excelValue ?? company.Text ?? company.Name ?? company.label ?? '',
    ).trim();
    if (!Utility_company_id && !name) return null;
    return { Utility_company_id, name };
}

/** Resolve the 2-letter state code GetCompanies expects. */
export function resolveUtilityCompanyState(
    form: Pick<PreContractIntakeForm, 'installState'>,
    opportunityDetails: any,
): string {
    const installAddr = opportunityDetails?.InstallationAddress;
    const raw =
        form.installState ||
        installAddr?.stateCode ||
        installAddr?.State ||
        installAddr?.state ||
        '';
    return String(raw).trim().toUpperCase();
}

/** Load the GetCompanies utility catalog for a state (Utility_company_id = Value). */
export async function fetchUtilityCompanyCatalog(
    state: string,
    financePartnerId?: number | string,
): Promise<PreContractUtilityCompanyCatalogItem[]> {
    const stateCode = String(state ?? '').trim();
    if (!stateCode) return [];
    try {
        const response = await masterServices.GetCompanies(stateCode, financePartnerId);
        const actualData = response?.data ?? response;
        if (!Array.isArray(actualData)) return [];
        return actualData
            .map((company: Record<string, unknown>) => mapGetCompaniesRow(company))
            .filter((item): item is PreContractUtilityCompanyCatalogItem => item != null);
    } catch {
        return [];
    }
}

/** Match a pre-contract utility label to GetCompanies Utility_company_id. */
export function resolveUtilityCompanyCatalogMatch(
    selectedName: string,
    companies: PreContractUtilityCompanyCatalogItem[],
): PreContractUtilityCompanyCatalogItem | null {
    const name = selectedName.trim();
    if (!name || companies.length === 0) return null;
    const normalized = normalizeCompanyName(name);
    const exact = companies.find((item) => normalizeCompanyName(item.name) === normalized);
    if (exact) return exact;
    return (
        companies.find((item) => {
            const optionNorm = normalizeCompanyName(item.name);
            return optionNorm && (normalized.includes(optionNorm) || optionNorm.includes(normalized));
        }) ?? null
    );
}

function formatRate(value: unknown): string {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return '';
    return String(num);
}

function formatAnnualUsage(value: number | null): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '';
    return String(Math.round(value));
}

export function mergeUtilityPrefill(
    form: PreContractIntakeForm,
    prefill: PreContractUtilityPrefill,
): Partial<PreContractIntakeForm> {
    const patch: Partial<PreContractIntakeForm> = {};
    if (!form.utilityCompany.trim() && prefill.utilityCompany) {
        patch.utilityCompany = prefill.utilityCompany;
    }
    if (!form.annualUsageKwh.trim() && prefill.annualUsageKwh) {
        patch.annualUsageKwh = prefill.annualUsageKwh;
    }
    if (!form.utilityRate.trim() && prefill.utilityRate) {
        patch.utilityRate = prefill.utilityRate;
    }
    return patch;
}

export function hasUtilityPrefill(prefill: PreContractUtilityPrefill | null): boolean {
    if (!prefill) return false;
    return !!(prefill.utilityCompany || prefill.annualUsageKwh || prefill.utilityRate);
}

/** Map the utility payload already loaded by the Dashboard V2 opportunity initializer. */
export function resolveUtilityPrefillFromStore(
    utilityDetails: unknown,
): PreContractUtilityPrefill | null {
    const details =
        utilityDetails && typeof utilityDetails === 'object'
            ? (utilityDetails as Record<string, unknown>)
            : null;
    const solarUtilityRaw = details?.SolarUtility ?? details?.solarUtility ?? details;
    const solarUtility =
        solarUtilityRaw && typeof solarUtilityRaw === 'object'
            ? (solarUtilityRaw as Record<string, unknown>)
            : null;
    if (!solarUtility) return null;

    const utilityCompany = resolveUtilityCompanyLabel(
        String(solarUtility.UtilityCompanyName ?? solarUtility.utilityCompanyName ?? ''),
    );
    const annualUsageKwh = formatAnnualUsage(
        effectiveAnnualUsageKwhForPricing(solarUtility, null),
    );
    const utilityRate = formatRate(
        solarUtility.CurrentAllInUtilityRate ??
            solarUtility.Utility_Rate ??
            solarUtility.utilityRate,
    );

    if (!utilityCompany && !annualUsageKwh && !utilityRate) return null;
    return { utilityCompany, annualUsageKwh, utilityRate };
}

export async function fetchUtilityPrefillFromOpportunity(
    salesForceId: string,
): Promise<PreContractUtilityPrefill | null> {
    const sfid = salesForceId.trim();
    if (!sfid) return null;

    try {
        const detailRaw = await opportunityInfoService.getOpportunityDetail(sfid);
        const opportunity = detailRaw != null ? (detailRaw?.data ?? detailRaw) : null;
        const opportunityId = opportunity?.Opportunity_Id;
        const opportunityIdStr =
            opportunityId != null && String(opportunityId).trim() !== '' ? String(opportunityId) : null;

        if (!opportunityIdStr) return null;

        const utilityResult = await designServices.RetrieveUtilityInformation(opportunityIdStr).catch(() => null);
        return resolveUtilityPrefillFromStore(utilityResult?.utilityDetails);
    } catch {
        return null;
    }
}

/** Snapshot of the four utility popover fields used to detect unsaved changes. */
export type PreContractUtilityDetailsSnapshot = {
    escalator: number;
    company: string;
    companyId: string;
    usage: string;
    rate: string;
};

export type PreContractUtilityDetailChange = {
    id: 'escalator' | 'company' | 'usage' | 'rate';
    label: string;
    from: string;
    to: string;
};

export function preContractUtilityDetailsSnapshot(
    form: Pick<
        PreContractIntakeForm,
        'utilityCompany' | 'utilityCompanyId' | 'annualUsageKwh' | 'utilityRate' | 'targetEscalator'
    >,
): PreContractUtilityDetailsSnapshot {
    return {
        escalator: resolvePreContractEscalator(form.targetEscalator),
        company: form.utilityCompany.trim(),
        companyId: String(form.utilityCompanyId ?? '').trim(),
        usage: form.annualUsageKwh.trim(),
        rate: form.utilityRate.trim(),
    };
}

export function preContractUtilityDetailsSignature(
    form: Pick<
        PreContractIntakeForm,
        'utilityCompany' | 'utilityCompanyId' | 'annualUsageKwh' | 'utilityRate' | 'targetEscalator'
    >,
): string {
    return JSON.stringify(preContractUtilityDetailsSnapshot(form));
}

function formatUsageChange(value: string): string {
    const n = Number(String(value).replace(/,/g, '').trim());
    if (Number.isFinite(n) && n > 0) return `${Math.round(n).toLocaleString()} kWh`;
    return value.trim() || '—';
}

function formatAllInRateChange(value: string): string {
    const n = Number(String(value).trim());
    if (Number.isFinite(n) && n > 0) return n.toFixed(3);
    return value.trim() || '—';
}

function parseUtilitySnapshot(raw: string | null): PreContractUtilityDetailsSnapshot | null {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw) as Partial<PreContractUtilityDetailsSnapshot>;
        if (!parsed || typeof parsed !== 'object') return null;
        return {
            escalator: resolvePreContractEscalator(parsed.escalator),
            company: String(parsed.company ?? '').trim(),
            companyId: String(parsed.companyId ?? '').trim(),
            usage: String(parsed.usage ?? '').trim(),
            rate: String(parsed.rate ?? '').trim(),
        };
    } catch {
        return null;
    }
}

function normalizeUtilityName(value: string): string {
    return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function listPreContractUtilityDetailChanges(
    savedSignature: string | null,
    form: Pick<
        PreContractIntakeForm,
        'utilityCompany' | 'utilityCompanyId' | 'annualUsageKwh' | 'utilityRate' | 'targetEscalator'
    >,
): PreContractUtilityDetailChange[] {
    const saved = parseUtilitySnapshot(savedSignature);
    if (!saved) return [];
    const current = preContractUtilityDetailsSnapshot(form);
    const changes: PreContractUtilityDetailChange[] = [];
    if (saved.escalator !== current.escalator) {
        changes.push({
            id: 'escalator',
            label: 'Escalator',
            from: formatSandboxEscalator(saved.escalator),
            to: formatSandboxEscalator(current.escalator),
        });
    }
    if (normalizeUtilityName(saved.company) !== normalizeUtilityName(current.company)) {
        changes.push({
            id: 'company',
            label: 'Utility provider',
            from: saved.company || '—',
            to: current.company || '—',
        });
    }
    if (saved.usage !== current.usage) {
        changes.push({
            id: 'usage',
            label: 'Annual usage',
            from: formatUsageChange(saved.usage),
            to: formatUsageChange(current.usage),
        });
    }
    if (saved.rate !== current.rate) {
        changes.push({
            id: 'rate',
            label: 'All-in utility rate',
            from: formatAllInRateChange(saved.rate),
            to: formatAllInRateChange(current.rate),
        });
    }
    return changes;
}
